import React, { useState, useEffect } from 'react';
import { BarChart3, Fuel, Users, FileText, MapPin, TrendingUp, Trophy, RefreshCw } from 'lucide-react';
import { fetchFuelAnalytics, fetchCountriesForDropdown } from '../services/api.ts';
import { FuelAnalytics as FuelAnalyticsType } from '../types/fuel.ts';
import './FuelAnalytics.css';

const BADGE_ICONS: Record<string, string> = {
  newcomer: '📝',
  reporter: '📋',
  scout: '🏅',
  expert: '⭐',
  legend: '🏆',
};

const FuelAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<FuelAnalyticsType | null>(null);
  const [countries, setCountries] = useState<{ id: number; name: string }[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number>(1);
  const [days, setDays] = useState<number>(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCountriesForDropdown().then(data => setCountries(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [selectedCountry, days]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await fetchFuelAnalytics(selectedCountry, days);
      setAnalytics(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number | null) => cents !== null ? `$${(cents / 100).toFixed(1)}` : '—';

  if (loading && !analytics) {
    return (
      <div className="fuel-analytics-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading fuel analytics...</p>
        </div>
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="fuel-analytics-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="fuel-analytics-page">
      <div className="fa-header">
        <h2><BarChart3 size={24} /> Fuel Analytics</h2>
        <div className="fa-controls">
          <select value={selectedCountry} onChange={(e) => setSelectedCountry(parseInt(e.target.value))}>
            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button className="refresh-btn" onClick={loadAnalytics} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {analytics && (
        <>
          {/* Summary Cards */}
          <div className="fa-cards">
            <div className="fa-card">
              <div className="fa-card-icon stations"><MapPin size={22} /></div>
              <div className="fa-card-body">
                <p className="fa-card-value">{analytics.total_stations.toLocaleString()}</p>
                <p className="fa-card-label">Total Stations</p>
                {analytics.unverified_stations > 0 && (
                  <p className="fa-card-sub warn">{analytics.unverified_stations} unverified</p>
                )}
              </div>
            </div>
            <div className="fa-card">
              <div className="fa-card-icon prices"><Fuel size={22} /></div>
              <div className="fa-card-body">
                <p className="fa-card-value">{analytics.active_prices.toLocaleString()}</p>
                <p className="fa-card-label">Active Prices</p>
              </div>
            </div>
            <div className="fa-card">
              <div className="fa-card-icon reports"><FileText size={22} /></div>
              <div className="fa-card-body">
                <p className="fa-card-value">{analytics.reports_today.toLocaleString()}</p>
                <p className="fa-card-label">Reports Today</p>
                <p className="fa-card-sub">{analytics.total_reports.toLocaleString()} in period</p>
              </div>
            </div>
            <div className="fa-card">
              <div className="fa-card-icon reporters"><Users size={22} /></div>
              <div className="fa-card-body">
                <p className="fa-card-value">{analytics.unique_reporters.toLocaleString()}</p>
                <p className="fa-card-label">Unique Reporters</p>
              </div>
            </div>
          </div>

          {/* Average Prices */}
          {analytics.avg_prices.length > 0 && (
            <div className="fa-section">
              <h3><TrendingUp size={18} /> Average Prices by Fuel Type</h3>
              <div className="fa-price-grid">
                {analytics.avg_prices.map(ap => (
                  <div key={ap.code} className="fa-price-card">
                    <div className="fa-price-code">{ap.code}</div>
                    <div className="fa-price-name">{ap.name}</div>
                    <div className="fa-price-avg">{formatPrice(ap.avg_price)}</div>
                    <div className="fa-price-range">
                      {formatPrice(ap.min_price)} — {formatPrice(ap.max_price)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reports per Day Chart (simple bar visualization) */}
          {analytics.reports_per_day.length > 0 && (
            <div className="fa-section">
              <h3><BarChart3 size={18} /> Reports per Day</h3>
              <div className="fa-bar-chart">
                {(() => {
                  const maxCount = Math.max(...analytics.reports_per_day.map(d => d.count), 1);
                  return analytics.reports_per_day.map(d => (
                    <div key={d.date} className="fa-bar-item">
                      <div className="fa-bar-wrapper">
                        <div
                          className="fa-bar"
                          style={{ height: `${(d.count / maxCount) * 100}%` }}
                          title={`${d.date}: ${d.count} reports`}
                        ></div>
                      </div>
                      <span className="fa-bar-label">{d.date.slice(5)}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Reports by Fuel Type */}
          {analytics.reports_by_type.length > 0 && (
            <div className="fa-section">
              <h3><Fuel size={18} /> Reports by Fuel Type</h3>
              <div className="fa-type-bars">
                {(() => {
                  const maxCount = Math.max(...analytics.reports_by_type.map(t => t.count), 1);
                  return analytics.reports_by_type.map(t => (
                    <div key={t.code} className="fa-type-bar-row">
                      <span className="fa-type-label">{t.code}</span>
                      <div className="fa-type-bar-bg">
                        <div className="fa-type-bar-fill" style={{ width: `${(t.count / maxCount) * 100}%` }}></div>
                      </div>
                      <span className="fa-type-count">{t.count.toLocaleString()}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          <div className="fa-two-col">
            {/* Top Stations */}
            {analytics.top_stations.length > 0 && (
              <div className="fa-section">
                <h3><MapPin size={18} /> Top Stations (Most Reported)</h3>
                <table className="fa-mini-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Station</th>
                      <th>Brand</th>
                      <th>Reports</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_stations.map((s, i) => (
                      <tr key={s.id}>
                        <td className="rank">{i + 1}</td>
                        <td>
                          <span className="name">{s.name}</span>
                          <span className="sub">{s.suburb || ''}</span>
                        </td>
                        <td>{s.brand}</td>
                        <td className="count">{s.report_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Top Reporters */}
            {analytics.top_reporters.length > 0 && (
              <div className="fa-section">
                <h3><Trophy size={18} /> Top Reporters</h3>
                <table className="fa-mini-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Reporter</th>
                      <th>Badge</th>
                      <th>Points</th>
                      <th>Reports</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_reporters.map((r, i) => (
                      <tr key={r.user_id}>
                        <td className="rank">{i + 1}</td>
                        <td className="name">{r.name}</td>
                        <td>
                          <span className="badge-display">
                            {BADGE_ICONS[r.badge] || '📝'} {r.badge}
                          </span>
                        </td>
                        <td className="count">{r.points.toLocaleString()}</td>
                        <td className="count">{r.total_reports}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FuelAnalytics;
